import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { Trophy, Mic2, MessageSquare, TrendingUp, Filter, Crown } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;

const getTierColor = (tier) => {
    const colors = {
        rookie: 'bg-zinc-600',
        contender: 'bg-blue-500',
        prospect: 'bg-violet-500',
        pro: 'bg-fuchsia-500',
        elite: 'bg-amber-500',
        legend: 'bg-[#7FFF00] text-black'
    };
    return colors[tier] || colors.rookie;
};

const getTierBorderColor = (tier) => {
    const colors = {
        rookie: 'border-zinc-600',
        contender: 'border-blue-500',
        prospect: 'border-violet-500',
        pro: 'border-fuchsia-500',
        elite: 'border-amber-500',
        legend: 'border-[#7FFF00]'
    };
    return colors[tier] || colors.rookie;
};

const LeaderboardPage = () => {
    const [leaderboard, setLeaderboard] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterTier, setFilterTier] = useState('all');

    useEffect(() => {
        fetchLeaderboard();
    }, [filterTier]);

    const fetchLeaderboard = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ limit: '50' });
            if (filterTier !== 'all') {
                params.append('tier', filterTier);
            }
            const response = await axios.get(`${API}/leaderboard?${params}`);
            setLeaderboard(response.data);
        } catch (error) {
            console.error('Failed to fetch leaderboard:', error);
        } finally {
            setLoading(false);
        }
    };

    const getRankDisplay = (rank) => {
        if (rank === 1) return { icon: <Crown className="w-5 h-5 text-[#7FFF00]" />, color: 'text-[#7FFF00]' };
        if (rank === 2) return { icon: <Trophy className="w-5 h-5 text-amber-400" />, color: 'text-amber-400' };
        if (rank === 3) return { icon: <Trophy className="w-5 h-5 text-amber-600" />, color: 'text-amber-600' };
        return { icon: null, color: 'text-zinc-400' };
    };

    return (
        <div className="max-w-4xl mx-auto px-4 py-8">
            {/* Header */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
                <div>
                    <h1 className="font-display font-bold text-2xl sm:text-3xl flex items-center gap-3">
                        <Trophy className="w-8 h-8 text-[#7FFF00]" />
                        THE <span className="text-[#7FFF00]">RANKINGS</span>
                    </h1>
                    <p className="text-zinc-400 text-sm mt-1">
                        Merit-based rankings. Earned through skill, not hype.
                    </p>
                </div>

                <Select value={filterTier} onValueChange={setFilterTier}>
                    <SelectTrigger className="w-40 bg-paper border-white/10 rounded-sm" data-testid="tier-filter">
                        <Filter className="w-4 h-4 mr-2 text-zinc-400" />
                        <SelectValue placeholder="Filter by tier" />
                    </SelectTrigger>
                    <SelectContent className="bg-paper border-white/10">
                        <SelectItem value="all">All Tiers</SelectItem>
                        <SelectItem value="legend">Legend</SelectItem>
                        <SelectItem value="elite">Elite</SelectItem>
                        <SelectItem value="pro">Pro</SelectItem>
                        <SelectItem value="prospect">Prospect</SelectItem>
                        <SelectItem value="contender">Contender</SelectItem>
                        <SelectItem value="rookie">Rookie</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {/* Tier Legend */}
            <div className="glass-panel rounded-sm p-4 mb-8">
                <h3 className="text-xs text-zinc-500 uppercase tracking-wider mb-3">Tier System</h3>
                <div className="flex flex-wrap gap-2">
                    {[
                        { name: 'Legend', min: '2000+', color: 'bg-[#7FFF00] text-black' },
                        { name: 'Elite', min: '1000+', color: 'bg-amber-500 text-black' },
                        { name: 'Pro', min: '600+', color: 'bg-fuchsia-500' },
                        { name: 'Prospect', min: '300+', color: 'bg-violet-500' },
                        { name: 'Contender', min: '100+', color: 'bg-blue-500' },
                        { name: 'Rookie', min: '0+', color: 'bg-zinc-600' },
                    ].map((tier) => (
                        <div key={tier.name} className="flex items-center gap-2">
                            <Badge className={`${tier.color} text-xs font-mono uppercase`}>
                                {tier.name}
                            </Badge>
                            <span className="text-xs text-zinc-500 font-mono">{tier.min}</span>
                        </div>
                    ))}
                </div>
            </div>

            {/* Founding-era banner — true while ranks are unclaimed; converts a thin board into a head start */}
            {!loading && leaderboard.length < 12 && (
                <div className="glass-panel rounded-sm p-5 mb-6 border border-primary/30 bg-gradient-to-r from-primary/10 to-transparent">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                        <div>
                            <div className="font-mono text-[11px] uppercase tracking-widest text-neon-blue mb-1">Founding Era · Ranks unclaimed</div>
                            <h3 className="font-display font-bold text-lg sm:text-xl">Six tiers. Nobody's claimed the top yet.</h3>
                            <p className="text-zinc-400 text-sm mt-1 max-w-2xl">
                                Rank is forged from real judgments — never bought. The board just opened, so the climb
                                has never been shorter. Drop a track, get judged, put your name on the wall first.
                            </p>
                        </div>
                        <Link to="/upload" className="shrink-0 inline-flex items-center bg-primary hover:bg-primary/90 text-white font-bold uppercase tracking-widest rounded-sm px-5 py-2.5 text-sm">
                            Claim Your Rank
                        </Link>
                    </div>
                </div>
            )}

            {/* Leaderboard Table */}
            <div className="glass-panel rounded-sm overflow-hidden" data-testid="leaderboard-table">
                {/* Table Header */}
                <div className="grid grid-cols-12 gap-4 p-4 bg-black/30 border-b border-white/10 text-xs text-zinc-500 uppercase tracking-wider font-mono">
                    <div className="col-span-1 text-center">#</div>
                    <div className="col-span-4 sm:col-span-3">Artist</div>
                    <div className="col-span-2 hidden sm:block text-center">Tier</div>
                    <div className="col-span-3 sm:col-span-2 text-center">Score</div>
                    <div className="col-span-2 hidden sm:block text-center">Perfs</div>
                    <div className="col-span-4 sm:col-span-2 text-center">Avg Rating</div>
                </div>

                {/* Table Body */}
                {loading ? (
                    <div className="divide-y divide-white/5">
                        {Array.from({ length: 10 }).map((_, i) => (
                            <div key={i} className="grid grid-cols-12 gap-4 p-4 items-center">
                                <div className="col-span-1">
                                    <Skeleton className="h-6 w-6 mx-auto bg-white/5 rounded-full" />
                                </div>
                                <div className="col-span-4 sm:col-span-3">
                                    <Skeleton className="h-4 w-24 bg-white/5" />
                                </div>
                                <div className="col-span-2 hidden sm:block">
                                    <Skeleton className="h-5 w-16 mx-auto bg-white/5" />
                                </div>
                                <div className="col-span-3 sm:col-span-2">
                                    <Skeleton className="h-4 w-12 mx-auto bg-white/5" />
                                </div>
                                <div className="col-span-2 hidden sm:block">
                                    <Skeleton className="h-4 w-8 mx-auto bg-white/5" />
                                </div>
                                <div className="col-span-4 sm:col-span-2">
                                    <Skeleton className="h-4 w-10 mx-auto bg-white/5" />
                                </div>
                            </div>
                        ))}
                    </div>
                ) : leaderboard.length > 0 ? (
                    <div className="divide-y divide-white/5">
                        {leaderboard.map((entry) => {
                            const rankDisplay = getRankDisplay(entry.rank);
                            return (
                                <Link
                                    key={entry.user_id}
                                    to={`/profile/${entry.user_id}`}
                                    className={`grid grid-cols-12 gap-4 p-4 items-center hover:bg-white/5 transition-colors ${
                                        entry.rank <= 3 ? `border-l-2 ${getTierBorderColor(entry.tier)}` : ''
                                    }`}
                                    data-testid={`leaderboard-entry-${entry.rank}`}
                                >
                                    {/* Rank */}
                                    <div className="col-span-1 text-center">
                                        {rankDisplay.icon || (
                                            <span className={`font-mono text-lg font-bold ${rankDisplay.color}`}>
                                                {entry.rank}
                                            </span>
                                        )}
                                    </div>

                                    {/* Artist */}
                                    <div className="col-span-4 sm:col-span-3">
                                        <p className="font-medium truncate">{entry.artist_name || entry.username}</p>
                                        <p className="text-xs text-zinc-500 truncate">@{entry.username}</p>
                                    </div>

                                    {/* Tier */}
                                    <div className="col-span-2 hidden sm:flex justify-center">
                                        <Badge className={`${getTierColor(entry.tier)} text-xs font-mono uppercase`}>
                                            {entry.tier}
                                        </Badge>
                                    </div>

                                    {/* Score */}
                                    <div className="col-span-3 sm:col-span-2 text-center">
                                        <span className={`font-mono text-lg font-bold ${
                                            entry.tier === 'legend' ? 'text-[#7FFF00]' : 'text-white'
                                        }`}>
                                            {entry.rank_score.toFixed(0)}
                                        </span>
                                    </div>

                                    {/* Performances */}
                                    <div className="col-span-2 hidden sm:flex justify-center items-center gap-1 text-zinc-400">
                                        <Mic2 className="w-3 h-3" />
                                        <span className="font-mono text-sm">{entry.total_performances}</span>
                                    </div>

                                    {/* Avg Rating */}
                                    <div className="col-span-4 sm:col-span-2 text-center">
                                        <span className="font-mono text-sm text-zinc-300">
                                            {entry.avg_rating.toFixed(1)}
                                        </span>
                                        <span className="text-xs text-zinc-500">/10</span>
                                    </div>
                                </Link>
                            );
                        })}
                    </div>
                ) : (
                    <div className="text-center py-16">
                        <TrendingUp className="w-12 h-12 mx-auto text-zinc-600 mb-4" />
                        <h3 className="font-display font-bold text-lg mb-2">No rankings yet</h3>
                        <p className="text-zinc-400 text-sm">
                            {filterTier !== 'all' 
                                ? `No artists in ${filterTier} tier yet`
                                : 'Upload performances and earn judgments to appear here'
                            }
                        </p>
                    </div>
                )}
            </div>

            {/* Stats */}
            <div className="mt-8 grid grid-cols-3 gap-4">
                <div className="glass-panel rounded-sm p-4 text-center">
                    <p className="font-mono text-2xl font-bold text-primary">{leaderboard.length}</p>
                    <p className="text-xs text-zinc-500 uppercase tracking-wider">Ranked Artists</p>
                </div>
                <div className="glass-panel rounded-sm p-4 text-center">
                    <p className="font-mono text-2xl font-bold text-secondary">
                        {leaderboard.reduce((sum, e) => sum + e.total_performances, 0)}
                    </p>
                    <p className="text-xs text-zinc-500 uppercase tracking-wider">Total Performances</p>
                </div>
                <div className="glass-panel rounded-sm p-4 text-center">
                    <p className="font-mono text-2xl font-bold text-[#7FFF00]">
                        {leaderboard.filter(e => e.tier === 'legend').length}
                    </p>
                    <p className="text-xs text-zinc-500 uppercase tracking-wider">Legends</p>
                </div>
            </div>
        </div>
    );
};

export default LeaderboardPage;
